import { type SelectOption } from '@framework/form/inputs/SelectInput.tsx';
import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import { IconEraser } from '@tabler/icons-react';
import { Button, type ButtonProps, Flex } from 'antd';
import clx from 'classnames';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import { TbRefresh } from 'react-icons/tb';
import styled from 'styled-components';
import { IconWrapper } from '../../icon/IconWrapper.tsx';
import { TbIcon } from '../../icon/TbIcon.tsx';
import { FlexRow } from '../../row/FlexRow.tsx';
import { type SearchableField } from '../ISearchableField.tsx';
import { AdminMessageFilteredList } from '../message/AdminMessageFilteredList.tsx';
import { FilterBarChipRow } from './chip/FilterBarChipRow.tsx';
import { type IFilterBarLayout } from './FilterBar.tsx';
import { useShownFilterNames } from './hooks/useShownFilterNames.ts';
import { FilterBarAddPopover } from './popover/FilterBarAddPopover.tsx';
import { FilterBarSortPopover } from './popover/FilterBarSortPopover.tsx';

const LayoutRowWrapper = styled.div`
  --row-padding-base: 8px;
  padding-block: var(--row-padding-base);
  background-color: transparent;
  & + & {
    border-top: 1px solid ${props => props.theme.colorSplit};
  }
  &:first-child {
    padding-top: 0;
     &.filter-bar-in-card {
      padding-top: var(--row-padding-base);
    }
  }
  &.filter-bar-in-card {
    padding-inline: var(--row-padding-base);
  }
  &.filter-bar-results-banner {
    padding-block: 4px;
  }
`;

export interface IFilterBarLayoutProps {
  id: string;
  fields: SearchableField[];
  query: Record<string, any>;
  /**
   * Schema dei filtri. Serve al `FormHandler` annidato del popover edit per
   * castare/validare i valori in modo coerente col form esterno.
   */
  schema: YupAnyObjectSchema;
  disableLocalStorage?: boolean;
  isFiltered: boolean;
  total?: number;
  /** Reset di default a `defaultQuery` (fallback del pulsante reset del banner). */
  onResetAll: () => void;
  /** Se presente, mostra il bottone "Aggiorna" standard a destra della toolbar. */
  onRefresh?: () => void;
  /** Azioni custom aggiuntive a destra della toolbar (es. Colonne, Esporta CSV). */
  extraActions?: React.ReactNode;
  /**
   * Campi ordinabili. Se presenti, accanto a "Filtri di Ricerca" compare il
   * bottone permanente "Ordinamento" (`FilterBarSortPopover`).
   */
  sortingFields?: SelectOption[] | null;
  /** Override del messaggio del banner "N elementi trovati…". */
  messageListMessage?: React.ReactNode;
  /**
   * Props del pulsante di reset (onClick custom, label, …). Replica il
   * comportamento del vecchio `AdvancedSearchCollapseForm`: spreadato sui
   * pulsanti "Rimuovi tutti", il suo `onClick` sovrascrive il reset di default.
   */
  resetButtonProps?: ButtonProps;
  /** Opzioni di layout per la FilterBar. */
  layout?: IFilterBarLayout;
}

/**
 * Composer interno della `FilterBar`. Possiede lo stato `shownFieldNames`
 * (via `useShownFilterNames`) e orchestra le tre righe del componente:
 *
 * 1. **Toolbar** — bottone "Filtri di Ricerca" + `extraActions`.
 * 2. **Riga chip** — chip per ogni filtro visibile + "+ Filtro".
 * 3. **Banner risultati** — messaggio "N elementi trovati…" con link reset
 *
 * La variante "in card" (`layout.showInCard`)
 * aggiunge padding per integrarsi in un Card.
 */
export function FilterBarLayout({
  id,
  fields,
  query,
  schema,
  disableLocalStorage,
  isFiltered,
  total,
  onResetAll,
  onRefresh,
  extraActions,
  sortingFields,
  messageListMessage,
  resetButtonProps,
  layout,
}: Readonly<IFilterBarLayoutProps>) {
  const { t } = useTranslation();
  const { shownFieldNames, addField, removeField, clearFields } =
    useShownFilterNames({
      id,
      fields,
      query,
      disableLocalStorage,
    });

  const layoutRowClassName = clx({ 'filter-bar-in-card': layout?.showInCard });

  // "Pulisci" = rimuovi tutti i chip + ESATTAMENTE lo stesso reset del banner
  // "Rimuovi filtri" (uniforme, nessuna sorpresa: "pulisci" azzera tutto): usa
  // l'onClick custom del consumer se presente, altrimenti il reset di default.
  const handleClear = () => {
    clearFields();
    if (resetButtonProps?.onClick) {
      resetButtonProps.onClick({} as React.MouseEvent<HTMLElement>);
    } else {
      onResetAll();
    }
  };

  return (
    <Flex vertical>
      <LayoutRowWrapper className={layoutRowClassName}>
        <FlexRow
          right={
            onRefresh || extraActions ? (
              <Flex gap={8} align="center">
                {onRefresh ? (
                  <Button
                    size="small"
                    type="text"
                    icon={<IconWrapper icon={<TbRefresh />} />}
                    onClick={onRefresh}
                  >
                    {t('filter-bar.refresh', 'Aggiorna')}
                  </Button>
                ) : null}
                {extraActions}
              </Flex>
            ) : undefined
          }
        >
          <Flex gap={8} align="center">
            <FilterBarAddPopover
              variant="toolbar"
              fields={fields}
              query={query}
              schema={schema}
              excludeNames={shownFieldNames}
              onAdded={addField}
            />
            {/* Bottone di ordinamento, accanto a "Filtri di Ricerca".
                Compare solo se il consumer passa `sortingFields`. */}
            {sortingFields?.length ? (
              <FilterBarSortPopover sortingFields={sortingFields} />
            ) : null}
          </Flex>
        </FlexRow>
      </LayoutRowWrapper>

      {shownFieldNames.length > 0 && (
        <LayoutRowWrapper className={layoutRowClassName}>
          {/* Slot `right` del `FlexRow`: il pulsante "Pulisci" (rimuove tutti i
              chip), messo vicino alla collezione di chip su cui agisce. */}
          <FlexRow
            right={
              <Button
                size="middle"
                icon={<TbIcon icon={IconEraser} />}
                onClick={handleClear}
              >
                {t('filter-bar.clear', 'Pulisci')}
              </Button>
            }
          >
            <FilterBarChipRow
              fields={fields}
              query={query}
              schema={schema}
              shownFieldNames={shownFieldNames}
              onAdded={addField}
              onRemoved={removeField}
            />
          </FlexRow>
        </LayoutRowWrapper>
      )}

      {/* Unico punto di reset: il pulsante accanto al banner risultati.
          Onora `resetButtonProps` (onClick/label custom), fallback al default. */}
      {isFiltered && (
        <LayoutRowWrapper
          className={clx(layoutRowClassName, 'filter-bar-results-banner')}
        >
          <AdminMessageFilteredList
            align="flex-start"
            style={{ marginBottom: 0 }}
            visible={isFiltered}
            items={total ?? null}
            message={
              messageListMessage ??
              t(
                'filter-bar.filtered-results-message',
                'elementi trovati in base ai filtri di ricerca.',
              )
            }
            action={
              <Button
                type="link"
                size="small"
                onClick={onResetAll}
                {...resetButtonProps}
              >
                {resetButtonProps?.children ??
                  t('filter-bar.remove-all', 'Rimuovi tutti')}
              </Button>
            }
          />
        </LayoutRowWrapper>
      )}
    </Flex>
  );
}
